import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Globe, User, LogOut, Menu, X, ShieldCheck } from "lucide-react";
import { useLang, LANGS } from "../i18n/LanguageContext";
import { useAuth } from "../auth/AuthContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";

export function Navbar() {
  const { t, lang, setLang } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const links = [
    { to: "/#tedaviler", label: t("nav_treatments"), id: "nav-link-treatments" },
    { to: "/#hastaneler", label: t("nav_hospitals"), id: "nav-link-hospitals" },
    { to: "/#surec", label: t("nav_process"), id: "nav-link-process" },
    { to: "/#sihirbaz", label: t("nav_wizard"), id: "nav-link-wizard" },
    { to: "/teknoloji", label: t("nav_tech"), id: "nav-link-tech" },
    { to: "/haberler", label: t("nav_news"), id: "nav-link-news" },
    { to: "/#dergi", label: t("nav_magazine"), id: "nav-link-magazine" },
    { to: "/#sss", label: t("nav_faq"), id: "nav-link-faq" },
    { to: "/belgeler", label: t("nav_documents"), id: "nav-link-documents" },
    { to: "/#iletisim", label: t("nav_contact"), id: "nav-link-contact" },
  ];

  const go = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (window.location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
      window.scrollTo(0, 0);
    }
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 glass border-b border-black/5">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 shrink-0" onClick={() => window.scrollTo(0, 0)}>
          <img src="/logo.png" alt="2D Healthcare" className="h-9 w-auto object-contain" />
          <div className="leading-none">
            <div className="font-heading font-bold tracking-tight text-ink">2D Healthcare</div>
            <div className="text-[10px] text-sub tracking-widest uppercase">I-Cell Networks · Japan</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <button key={l.id} data-testid={l.id} onClick={() => go(l.to)} className="text-sm text-ink/70 hover:text-ink transition-colors duration-200 font-medium">
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="language-selector" className="flex items-center gap-1.5 px-3 h-9 rounded-full hover:bg-black/5 transition-colors text-sm font-semibold text-ink">
              <Globe size={15} strokeWidth={2.2} />
              <span>{lang.toUpperCase()}</span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-2xl">
              {LANGS.map((l) => (
                <DropdownMenuItem key={l.code} data-testid={`lang-option-${l.code}`} onClick={() => setLang(l.code)} className={l.code === lang ? "font-bold text-brand" : ""}>
                  <span className="w-7 text-xs text-sub">{l.short}</span> {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <>
              <Link to={user.role === "admin" ? "/admin" : "/panel"} data-testid="nav-portal-button" className="hidden sm:flex items-center gap-1.5 h-9 px-4 rounded-full bg-ink text-white text-sm font-semibold hover:bg-black transition-colors">
                {user.role === "admin" ? <ShieldCheck size={15} /> : <User size={15} />}
                {user.role === "admin" ? "Admin" : t("nav_portal")}
              </Link>
              <button data-testid="nav-logout-button" onClick={async () => { await logout(); navigate("/"); }} title={t("nav_logout")} className="w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center text-sub hover:text-ink transition-colors">
                <LogOut size={16} />
              </button>
            </>
          ) : (
            <Link to="/giris" data-testid="nav-login-button" className="hidden sm:flex h-9 px-4 rounded-full bg-ink text-white text-sm font-semibold items-center hover:bg-black transition-colors">
              {t("nav_login")}
            </Link>
          )}

          <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} className="lg:hidden w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center">
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="lg:hidden border-t border-black/5 glass px-6 py-4 flex flex-col gap-1">
          {links.map((l) => (
            <button key={l.id} data-testid={`mobile-${l.id}`} onClick={() => go(l.to)} className="text-left py-2.5 text-ink/80 font-medium border-b border-black/5">
              {l.label}
            </button>
          ))}
          <button data-testid="mobile-nav-portal" onClick={() => { setOpen(false); navigate(user ? (user.role === "admin" ? "/admin" : "/panel") : "/giris"); }} className="mt-3 h-11 rounded-full bg-ink text-white font-semibold">
            {user ? (user.role === "admin" ? "Admin" : t("nav_portal")) : t("nav_login")}
          </button>
        </motion.div>
      )}
    </header>
  );
}
